import { useSyncExternalStore } from 'react';

/** What the status bar shows (spec 6.1.8). */
export type ConnectionState = 'online' | 'reconnecting' | 'offline';

export const OFFLINE_RETRY_MS = 5000;

/**
 * Connection state of the page: offline after a request failed at network level (or the browser went
 * offline) until a request succeeds again; reconnecting while a run event stream retries. While offline it
 * calls the probe (`GET /config`) every 5 seconds.
 */
export class ConnectionMonitor {
  private networkDown = false;
  private readonly retryingStreams = new Set<object>();
  private readonly listeners = new Set<() => void>();
  private probe: (() => Promise<unknown>) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private state: ConnectionState = 'online';

  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getState = (): ConnectionState => this.state;

  /** A response arrived (any HTTP status). */
  requestSucceeded() {
    this.setNetworkDown(false);
  }

  /** No response: the server or the network is unreachable. */
  requestFailed() {
    this.setNetworkDown(true);
  }

  streamRetrying(stream: object) {
    this.retryingStreams.add(stream);
    this.update();
  }

  streamConnected(stream: object) {
    if (this.retryingStreams.delete(stream)) this.update();
  }

  /** The request used to check whether the API is back, while offline. Returns an unregister function. */
  setProbe(probe: () => Promise<unknown>): () => void {
    this.probe = probe;
    this.syncTimer();
    return () => {
      if (this.probe === probe) this.probe = null;
      this.syncTimer();
    };
  }

  reset() {
    this.networkDown = false;
    this.retryingStreams.clear();
    this.probe = null;
    this.syncTimer();
    this.update();
  }

  private setNetworkDown(down: boolean) {
    if (this.networkDown === down) return;
    this.networkDown = down;
    this.syncTimer();
    this.update();
  }

  private syncTimer() {
    const needed = this.networkDown && this.probe !== null;
    if (needed && this.timer === null) {
      this.timer = setInterval(() => {
        // The probe goes through the API client, which reports success or failure here.
        void this.probe?.().catch(() => {});
      }, OFFLINE_RETRY_MS);
    } else if (!needed && this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private update() {
    const next: ConnectionState = this.networkDown
      ? 'offline'
      : this.retryingStreams.size > 0
        ? 'reconnecting'
        : 'online';
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

export const connection = new ConnectionMonitor();

export function useConnectionState(): ConnectionState {
  return useSyncExternalStore(connection.subscribe, connection.getState, connection.getState);
}
